import React from "react";
import clsx from "clsx";
import styles from "../styles/AppointmentCard.module.css";

import { faCalendar } from "@fortawesome/free-regular-svg-icons";
import { APPOINTMENT_STATUS } from "../constants";
import { useAppointmentSummary } from "../hooks/useAppointmentSummary";
import { FaIcon } from "../shared/ui";
import { APPOINTMENT_STRINGS } from "../constants/Appointment";
import { parseDateForDisplay, isAppointmentExpired, getShortTimezone, formatAppointmentTime, isClosedAppointmentStatus, isOpenAppointmentStatus } from "../utils/chatUtils";
import { useAppSettings } from "../context/AppSettingsContext";

const AppointmentCard = ({ message, isMyMessage, isSender }) => {
    const appointmentMsg = message?.appointment || {};
    // Use the new hook
    const { openAppointmentSummary } = useAppointmentSummary();
    const { eventTimezone } = useAppSettings();

    // Get target user name - if it's my message, show the recipient's name from appointmentMsg
    // Otherwise, show the sender's name
    const targetUserName = React.useMemo(() => {
      if (appointmentMsg?.name) {
        return appointmentMsg.name; // This is the 'name' field from appointment object
      }
      if (message?.user?.name) {
        return message.user.name; // Fallback to message sender's name
      }
      return 'Unknown User';
    }, [appointmentMsg?.name, message?.user?.name]);

    // Helper function to parse time string (e.g., "10:45 PM")
    // Check if the appointment date AND time has passed
    const isExpired = React.useMemo(() => {
      return isAppointmentExpired(
        appointmentMsg?.date,
        appointmentMsg?.startTime,
        appointmentMsg?.endTime,
        eventTimezone
      );
    }, [appointmentMsg?.date, appointmentMsg?.startTime, appointmentMsg?.endTime, eventTimezone]);

    // Click handler to open the modal and pass details
    const handleCardClick = (event) => {
        openAppointmentSummary(message, isMyMessage, isSender, event);
    };
    // Requested and accepted appointments are still open; once past their end time they show as expired
    const isShowingExpired = isExpired && isOpenAppointmentStatus(appointmentMsg?.status);

    // Accepted appointments stay clickable so the preview can be opened to cancel them, unless expired
    const isClickDisabled = isClosedAppointmentStatus(appointmentMsg?.status) || isShowingExpired;

    const isCancelAppointment = [
      APPOINTMENT_STATUS.CANCELED,
    ].includes(appointmentMsg?.status);

    const getFormattedDate = (dateString) => {
      if (!dateString) return null;
      const date = parseDateForDisplay(dateString);
      return date.toLocaleDateString(APPOINTMENT_STRINGS.DATE_LOCALE, APPOINTMENT_STRINGS.DATE_FORMAT_OPTIONS);
    };

    const formattedDate = getFormattedDate(appointmentMsg?.date);

    const formatDuration = (totalMinutes) => {
      const mins = Number(totalMinutes) || 0;
      if (mins < 60) return `${mins}${APPOINTMENT_STRINGS.MINUTES_SUFFIX}`;
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return remainMins > 0
        ? `${hrs}${APPOINTMENT_STRINGS.HOURS_SUFFIX} ${remainMins}${APPOINTMENT_STRINGS.MINUTES_SHORT_SUFFIX}`
        : `${hrs}${APPOINTMENT_STRINGS.HOURS_SUFFIX}`;
    };

    const timezoneAbbr = getShortTimezone(eventTimezone);

    return (
      <div
        data-testid={APPOINTMENT_STRINGS.TEST_ID}
        data-message-id={message?.id}
        data-sender-id={isSender}
        className={clsx(styles.appointmentCard, {
          [styles.disabledCard]: isClickDisabled,
          [styles.cancelCard]: isCancelAppointment,
        })}
        onClick={isClickDisabled ? undefined : handleCardClick} // Attach click handler
      >
        <div className={styles.appointmentIcon}>
          <span role="img" aria-label={APPOINTMENT_STRINGS.CALENDAR_LABEL}>
            <FaIcon icon={faCalendar} size="xs"/>
          </span>
        </div>
        <div className={styles.appointmentDetails}>
          <div className={styles.appointmentTitle}>
            {appointmentMsg?.title || APPOINTMENT_STRINGS.DEFAULT_TITLE.replace('{targetUser}', targetUserName)}
          </div>
          {formattedDate && (
            <div>{formattedDate}</div>
          )}
          <div>
            {appointmentMsg?.startTime && appointmentMsg?.endTime
              ? `${formatAppointmentTime(appointmentMsg?.startTime)}${APPOINTMENT_STRINGS.TIME_SEPARATOR}${formatAppointmentTime(appointmentMsg?.endTime)} (${formatDuration(appointmentMsg?.totalMinutes)})${timezoneAbbr ? ` ${timezoneAbbr}` : ''}`
              : APPOINTMENT_STRINGS.NO_TIME_SPECIFIED}
          </div>
          {isShowingExpired ? (
            <div className={styles.appointmentStatusRed}>
              {APPOINTMENT_STRINGS.STATUS_EXPIRED}
            </div>
          ) : (
            <>
              {appointmentMsg?.status === APPOINTMENT_STATUS.REQUESTED && (
                <div className={styles.appointmentStatusRequested}>{APPOINTMENT_STRINGS.STATUS_REQUESTED}</div>
              )}
              {appointmentMsg?.status === APPOINTMENT_STATUS.REJECTED && (
                <div className={styles.appointmentStatusRed}>{APPOINTMENT_STRINGS.STATUS_DENIED}</div>
              )}
              {appointmentMsg?.status === APPOINTMENT_STATUS.ACCEPTED && (
                <div className={styles.appointmentStatusGreen}>{APPOINTMENT_STRINGS.STATUS_ACCEPTED}</div>
              )}
              {appointmentMsg?.status === APPOINTMENT_STATUS.CANCELED && (
                <div className={styles.appointmentStatusCancel}>
                  {isMyMessage ? APPOINTMENT_STRINGS.STATUS_CANCELED_SENDER : APPOINTMENT_STRINGS.STATUS_CANCELED_RECIPIENT}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
};

export default AppointmentCard;